<template>
  <div class="leaderboard-card">
    <div class="stats-panel-card">
      <div class="course-card-body">
        <div class="card-header">
          <h4 class="card-title">
            <i class="uil-trophy"></i>
            <span>{{ $t("general.my_ranking") }}</span>
          </h4>
        </div>

        <!-- Skeleton Loading -->
        <div v-if="loading" class="leaderboard-content">
          <div class="my-rank-panel skeleton-panel">
            <div class="skeleton skeleton-rank-lg"></div>
            <div class="skeleton skeleton-avatar"></div>
            <div class="skeleton skeleton-name"></div>
            <div class="skeleton skeleton-points"></div>
          </div>
          <div class="list-head">
            <span class="list-head__rank">#</span>
            <span class="list-head__user">{{ nameColumnLabel }}</span>
            <span class="list-head__points">{{ $t("point.point") }}</span>
          </div>
          <ul class="leaderboard-list">
            <li v-for="n in 5" :key="'skeleton_leader_' + n" class="leaderboard-item skeleton-item">
              <div class="skeleton skeleton-rank"></div>
              <div class="skeleton skeleton-avatar-small"></div>
              <div class="skeleton skeleton-name flex-grow"></div>
              <div class="skeleton skeleton-points"></div>
            </li>
          </ul>
        </div>

        <!-- Actual Content -->
        <div v-else class="leaderboard-content">
          <div class="my-rank-panel">
            <div class="my-rank-panel__rank" :title="$t('general.my_ranking')">
              <span class="my-rank-panel__rank-label">#</span>
              <span class="my-rank-panel__rank-value">{{ myRankDisplay }}</span>
            </div>
            <div class="my-rank-panel__user">
              <UserProfileAvatar
                :name="leaderBoard.user?.fullname || ''"
                :photo="leaderBoard.user?.photo_link"
                class="leaderboard-self-avatar"
              />
              <div class="my-rank-panel__meta">
                <span class="my-rank-panel__name">{{ leaderBoard.user?.fullname || "—" }}</span>
                <span class="my-rank-panel__subtitle">{{ $t("general.my_ranking") }}</span>
              </div>
            </div>
            <div class="my-rank-panel__points">
              <strong>{{ pointsCount }}</strong>
              <span>{{ $t("point.point") }}</span>
            </div>
          </div>

          <div class="list-head">
            <span class="list-head__rank">#</span>
            <span class="list-head__user">{{ nameColumnLabel }}</span>
            <span class="list-head__points">{{ $t("point.point") }}</span>
          </div>

          <ul class="leaderboard-list">
            <li
              v-for="(user_point, index) in leaderBoard.result"
              :key="'leader_' + index"
              class="leaderboard-item"
              :class="{
                'leaderboard-item--self': user_point.id == leaderBoard.user?.id,
                ['leaderboard-item--top-' + (index + 1)]: index < 3,
              }"
            >
              <span class="rank-badge" :class="'rank-badge--' + (index + 1)">
                {{ index + 1 }}
              </span>
              <span class="user-info">
                <UserProfileAvatar
                  :name="user_point.fullname || ''"
                  :photo="user_point.photo_link"
                  class="leaderboard-list-avatar"
                />
                <span class="user-name">{{ user_point.fullname }}</span>
              </span>
              <span class="user-points">
                {{ user_point.points_sum_point ? user_point.points_sum_point : 0 }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserProfileAvatar from "@/view/components/common/UserProfileAvatar.vue";

export default {
  name: "LeaderBoard",
  components: { UserProfileAvatar },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    leaderBoard: {
      type: Object,
      default: () => ({}),
    },
    pointsCount: {
      type: [Number, String],
      default: 0,
    },
  },
  computed: {
    myRankDisplay() {
      const index = this.leaderBoard?.userIndex;
      if (index === null || index === undefined || index === "") return "—";
      return String(index).replace(/^#\s*/, "");
    },
    nameColumnLabel() {
      const translated = this.$t("general.name");
      return translated === "general.name" ? "Ad Soyad" : translated;
    },
  },
};
</script>

<style scoped>
.leaderboard-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.stats-panel-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  overflow: hidden;
}

.course-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  min-width: 0;
}

.card-header {
  min-height: 0 !important;
  height: auto !important;
  padding: 0 0 8px !important;
  margin: 0 0 12px !important;
  border-bottom: 1px solid #e5e7eb;
  background: transparent;
}

.card-title {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.3;
  color: #0f172a;
}

.card-title i {
  flex-shrink: 0;
  margin-top: 1px;
  color: #64748b;
}

/* —— My rank panel —— */
.my-rank-panel {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 0 0 14px;
  margin-bottom: 14px;
  background: transparent;
  border: none;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 0;
}

.my-rank-panel__rank {
  display: flex;
  align-items: baseline;
  gap: 1px;
  min-width: 44px;
}

.my-rank-panel__rank-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
}

.my-rank-panel__rank-value {
  font-size: 1.375rem;
  font-weight: 700;
  line-height: 1;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.my-rank-panel__user {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.my-rank-panel__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.my-rank-panel__name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.my-rank-panel__subtitle {
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #94a3b8;
}

.my-rank-panel__points {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
  flex-shrink: 0;
}

.my-rank-panel__points strong {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.my-rank-panel__points span {
  font-size: 0.6875rem;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* —— Column headers —— */
.list-head {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 0 8px 8px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
  border-bottom: 1px solid #e2e8f0;
}

.list-head__points {
  text-align: right;
}

/* —— List —— */
.leaderboard-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.leaderboard-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.leaderboard-item {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 10px 8px;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s ease;
}

.leaderboard-item:last-child {
  border-bottom: none;
}

.leaderboard-item:hover {
  background-color: transparent;
}

.leaderboard-item--self {
  background-color: transparent;
}

.leaderboard-item--self .user-name {
  font-weight: 600;
  color: #0f172a;
}

.rank-badge {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #475569;
  background: transparent;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.rank-badge--1 {
  color: #92400e;
  background: transparent;
  border-color: #f59e0b;
}

.rank-badge--2 {
  color: #475569;
  background: transparent;
  border-color: #94a3b8;
}

.rank-badge--3 {
  color: #9a3412;
  background: transparent;
  border-color: #fb923c;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.leaderboard-self-avatar {
  width: 36px;
  height: 36px;
  min-width: 36px;
  flex-shrink: 0;
  font-size: 12px;
}

.leaderboard-list-avatar {
  width: 28px;
  height: 28px;
  min-width: 28px;
  flex-shrink: 0;
  font-size: 10px;
}

.user-name {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-points {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 40px;
}

/* —— Skeleton —— */
.skeleton {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-panel {
  pointer-events: none;
}

.skeleton-item:hover {
  background-color: transparent !important;
}

.skeleton-rank-lg {
  width: 36px;
  height: 28px;
}

.skeleton-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-avatar-small {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-rank {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  flex-shrink: 0;
}

.skeleton-name {
  width: 110px;
  height: 12px;
  flex-shrink: 0;
}

.skeleton-name.flex-grow {
  width: auto;
  flex: 1;
  min-width: 80px;
}

.skeleton-points {
  width: 48px;
  height: 12px;
  flex-shrink: 0;
  justify-self: end;
}

@media (max-width: 1024px) {
  .course-card-body {
    padding: 14px;
  }
}

@media (max-width: 768px) {
  .course-card-body {
    padding: 14px;
  }

  .card-header {
    margin-bottom: 10px !important;
  }

  .card-title {
    font-size: 0.875rem;
  }

  .my-rank-panel {
    gap: 10px;
    padding: 0 0 12px;
  }

  .my-rank-panel__rank-value {
    font-size: 1.25rem;
  }

  .user-name,
  .user-points {
    font-size: 0.8125rem;
  }
}

@media (max-width: 576px) {
  .course-card-body {
    padding: 14px 12px;
  }

  .my-rank-panel {
    grid-template-columns: auto 1fr;
    grid-template-areas:
      "rank user"
      "rank points";
    gap: 8px 10px;
  }

  .my-rank-panel__rank {
    grid-area: rank;
    align-self: center;
  }

  .my-rank-panel__user {
    grid-area: user;
  }

  .my-rank-panel__points {
    grid-area: points;
    align-items: flex-start;
    padding-left: 46px;
  }

  .list-head,
  .leaderboard-item {
    grid-template-columns: 32px 1fr auto;
    gap: 8px;
    padding-left: 4px;
    padding-right: 4px;
  }

  .rank-badge {
    width: 26px;
    height: 26px;
    font-size: 0.6875rem;
  }

  .leaderboard-list-avatar {
    width: 26px;
    height: 26px;
    min-width: 26px;
    font-size: 9px;
  }

  .user-name {
    font-size: 0.75rem;
  }

  .user-points {
    font-size: 0.75rem;
  }

  .my-rank-panel__subtitle {
    display: none;
  }
}

@media (max-width: 576px) {
  .leaderboard-item {
    min-height: 48px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .my-rank-panel {
    min-height: 64px;
  }

  .my-rank-panel__name,
  .user-name {
    font-size: 14px;
    line-height: 1.4;
  }

  .my-rank-panel__subtitle,
  .user-points {
    font-size: 12px;
    line-height: 1.4;
  }
}
</style>
